import {mapRecordToItems} from '@/helpers/mapRecordToItems.helper';

export enum QualityCasePriority {
    HIGH = 'high',
    NORMAL = 'normal',
    LOW = 'low',
}

export namespace QualityCasePriority {
    export const Record: Record<QualityCasePriority, { text: string; rank: number }> = {
        [QualityCasePriority.HIGH]: {text: 'Hoch', rank: 0},
        [QualityCasePriority.NORMAL]: {text: 'Normal', rank: 1},
        [QualityCasePriority.LOW]: {text: 'Niedrig', rank: 2},
    };
    export const Items = mapRecordToItems(Record);
}

/**
 * QM-Cockpit (workflow-manager `quality-cases`): one case = one call/decision of the QM person.
 * The absence early warning keeps its own records (AbsenceEarlyWarningRecord); the cockpit inbox merges them read-only.
 */
export enum QualityCaseType {
    /** "Rückgewinnung": a course termination was created */
    RESCUE = 'rescue',
    /** "Gruppe": the group of a course shrinks (termination in the course or below the minimum) */
    GROUP = 'group',
    /**
     * "Kontaktpunkt" (formerly "Erste 90 Tage"): one-time touchpoint of a new child / teacher / Standort; a child's
     * recurring contact ("alle X Unterrichtswochen") is an onboarding case with `recurring: true`
     */
    ONBOARDING = 'onboarding',
    /** "Lehrkraft": termination spike among the students of a teacher → call the teacher */
    TEACHER = 'teacher',
    /** "Standort": problems at a Standort (terminations, group cases, cancellations) → call the school */
    INSTITUTION = 'institution',
    /** "Regelgespräch": recurring contact ("alle X Unterrichtswochen") with a teacher or a Standort */
    CHECK_IN = 'checkIn',
    /** "Risiko": risk score of an active child above the threshold (Paket 2, optional) */
    RISK = 'risk',
    /** "Beschwerde": complaint recorded by hand (cockpit / profile of child, teacher or Standort) */
    COMPLAINT = 'complaint',
    /** "Übergang": child in the last primary grade (4; Berlin / Brandenburg 6) before the summer holidays (online / other Standort) */
    TRANSITION = 'transition',
}

export namespace QualityCaseType {
    export const Record: Record<QualityCaseType, { text: string }> = {
        [QualityCaseType.RESCUE]: {text: 'Rückgewinnung'},
        [QualityCaseType.GROUP]: {text: 'Gruppe'},
        [QualityCaseType.ONBOARDING]: {text: 'Kontaktpunkt'},
        [QualityCaseType.TEACHER]: {text: 'Lehrkraft'},
        [QualityCaseType.INSTITUTION]: {text: 'Standort'},
        [QualityCaseType.CHECK_IN]: {text: 'Regelgespräch'},
        [QualityCaseType.RISK]: {text: 'Risiko'},
        [QualityCaseType.COMPLAINT]: {text: 'Beschwerde'},
        // generic label; a single case names its own grade ("Übergang Klasse 6" in BE / BB)
        [QualityCaseType.TRANSITION]: {text: 'Übergang Klasse 4/6'},
    };
    export const Items = mapRecordToItems(Record);

    /** Default priority per type (inbox: priority, then due date) */
    export const DefaultPriority: Record<QualityCaseType, QualityCasePriority> = {
        [QualityCaseType.RESCUE]: QualityCasePriority.HIGH,
        [QualityCaseType.GROUP]: QualityCasePriority.NORMAL,
        [QualityCaseType.ONBOARDING]: QualityCasePriority.NORMAL,
        [QualityCaseType.TEACHER]: QualityCasePriority.HIGH,
        [QualityCaseType.INSTITUTION]: QualityCasePriority.HIGH,
        [QualityCaseType.CHECK_IN]: QualityCasePriority.LOW,
        [QualityCaseType.RISK]: QualityCasePriority.NORMAL,
        [QualityCaseType.COMPLAINT]: QualityCasePriority.HIGH,
        [QualityCaseType.TRANSITION]: QualityCasePriority.NORMAL,
    };
}

/** Who a check-in case is about */
export enum QualityCheckInTarget {
    TEACHER = 'teacher',
    INSTITUTION = 'institution',
}

export enum QualityCaseStatus {
    OPEN = 'open',
    DONE = 'done',
    /** Legacy (never set any more): closed by the system – treated like expired */
    CLOSED_AUTO = 'closedAuto',
    /** "Nicht bearbeitet": expired by the crons (termination effective, touchpoint/interval passed, trigger gone …) */
    EXPIRED = 'expired',
    /** "Verworfen": discarded by hand (POST quality-cases/:id/discard) */
    DISCARDED = 'discarded',
}

export namespace QualityCaseStatus {
    export const Record: Record<QualityCaseStatus, { text: string }> = {
        [QualityCaseStatus.OPEN]: {text: 'Offen'},
        [QualityCaseStatus.DONE]: {text: 'Erledigt'},
        [QualityCaseStatus.CLOSED_AUTO]: {text: 'Automatisch geschlossen'},
        [QualityCaseStatus.EXPIRED]: {text: 'Nicht bearbeitet'},
        [QualityCaseStatus.DISCARDED]: {text: 'Verworfen'},
    };
    export const Items = mapRecordToItems(Record);

    /** Closed without a decision (no outcome) */
    export const NOT_HANDLED: QualityCaseStatus[] = [QualityCaseStatus.EXPIRED, QualityCaseStatus.CLOSED_AUTO];
    /** Any closed status */
    export const CLOSED: QualityCaseStatus[] = [QualityCaseStatus.DONE, QualityCaseStatus.EXPIRED, QualityCaseStatus.DISCARDED, QualityCaseStatus.CLOSED_AUTO];
}

/** Target group of the contact points (one-time "Einmalig ab Start" + "Regelmäßig") and of a feedback request */
export enum QualityOnboardingTarget {
    /**
     * Child: new as long as its latest enabled one-time touchpoint + grace has not passed (from the first participation in
     * any course). Contact: the customer of the child's invoice cycle (the paying person), else the parents.
     */
    CHILD = 'child',
    /** Teacher: from the first course segment with the teacher as teacher ever (contact: the teacher) */
    TEACHER = 'teacher',
    /**
     * Standort: from the first running course of the Standort ever. Contact: Sekretariat + Ansprechpartner; when at least
     * one of them is marked `qmContact`, only the marked ones.
     */
    INSTITUTION = 'institution',
}

export namespace QualityOnboardingTarget {
    export const Record: Record<QualityOnboardingTarget, { text: string; plural: string }> = {
        [QualityOnboardingTarget.CHILD]: {text: 'Kind', plural: 'Kinder & Eltern'},
        [QualityOnboardingTarget.TEACHER]: {text: 'Lehrkraft', plural: 'Lehrkräfte'},
        [QualityOnboardingTarget.INSTITUTION]: {text: 'Standort', plural: 'Standorte'},
    };
    export const Items = mapRecordToItems(Record);
}

export enum QualityCaseOutcome {
    /** Rescue: termination undone, student stays in the course */
    STAYS = 'stays',
    /** Rescue: termination undone, student switches to another course (same subject) */
    OTHER_COURSE = 'otherCourse',
    /** Rescue: like otherCourse, target course is online (room isOnline) */
    ONLINE = 'online',
    /** Rescue: termination undone, course and billing paused until a date */
    PAUSED = 'paused',
    /** Student leaves (nothing is executed) */
    LEAVES = 'leaves',
    /** Not reached after maxAttempts */
    NOT_REACHED = 'notReached',
    /** Onboarding / group: everything fine */
    OK = 'ok',
    /** Onboarding / group: problem found (note) */
    PROBLEM = 'problem',
    /** Teacher / Standort / check-in: talk held (protocol entry with rating) */
    TALK_DONE = 'talkDone',
    /** Group: "Erledigt sich" – group full again / course ends (no measure needed) */
    RESOLVED = 'resolved',
    /** Check-in: merged into an open case of the same teacher / Standort (system; that case includes the check-in) */
    MERGED = 'merged',
    /** Complaint: solved (note) */
    SOLVED = 'solved',
    /** Complaint: not solved (note) */
    UNSOLVED = 'unsolved',
    /** Transition: continues online (switch is done in the profile, nothing executed here) */
    CONTINUES_ONLINE = 'continuesOnline',
    /** Transition: continues at another Standort (nothing executed here) */
    OTHER_INSTITUTION = 'otherInstitution',
    /** Transition: stays in the current course / at the Standort */
    CONTINUES_HERE = 'continuesHere',
}

export namespace QualityCaseOutcome {
    export const Record: Record<QualityCaseOutcome, { text: string }> = {
        [QualityCaseOutcome.STAYS]: {text: 'Bleibt'},
        [QualityCaseOutcome.OTHER_COURSE]: {text: 'Anderer Kurs'},
        [QualityCaseOutcome.ONLINE]: {text: 'Online weiter'},
        [QualityCaseOutcome.PAUSED]: {text: 'Pausiert'},
        [QualityCaseOutcome.LEAVES]: {text: 'Geht'},
        [QualityCaseOutcome.NOT_REACHED]: {text: 'Nicht erreicht'},
        [QualityCaseOutcome.OK]: {text: 'Alles gut'},
        [QualityCaseOutcome.PROBLEM]: {text: 'Problem'},
        [QualityCaseOutcome.TALK_DONE]: {text: 'Gespräch geführt'},
        [QualityCaseOutcome.RESOLVED]: {text: 'Erledigt sich'},
        [QualityCaseOutcome.MERGED]: {text: 'Zusammengeführt'},
        [QualityCaseOutcome.SOLVED]: {text: 'Gelöst'},
        [QualityCaseOutcome.UNSOLVED]: {text: 'Nicht gelöst'},
        [QualityCaseOutcome.CONTINUES_ONLINE]: {text: 'Weiter online'},
        [QualityCaseOutcome.OTHER_INSTITUTION]: {text: 'Anderer Standort'},
        [QualityCaseOutcome.CONTINUES_HERE]: {text: 'Bleibt am Standort'},
    };
    export const Items = mapRecordToItems(Record);

    /** Outcomes allowed per case type */
    export const ByType: Record<QualityCaseType, QualityCaseOutcome[]> = {
        [QualityCaseType.RESCUE]: [
            QualityCaseOutcome.STAYS, QualityCaseOutcome.OTHER_COURSE, QualityCaseOutcome.ONLINE, QualityCaseOutcome.PAUSED,
            QualityCaseOutcome.LEAVES, QualityCaseOutcome.NOT_REACHED,
        ],
        // multi-contact types: no case-level "nicht erreicht" (attempts per contact, POST quality-cases/:id/contacts/:key/attempt)
        [QualityCaseType.GROUP]: [QualityCaseOutcome.OK, QualityCaseOutcome.RESOLVED, QualityCaseOutcome.PROBLEM],
        // teacher / Standort onboarding: TALK_DONE (see ByOnboardingTarget)
        [QualityCaseType.ONBOARDING]: [QualityCaseOutcome.OK, QualityCaseOutcome.PROBLEM, QualityCaseOutcome.NOT_REACHED, QualityCaseOutcome.TALK_DONE],
        [QualityCaseType.TEACHER]: [QualityCaseOutcome.TALK_DONE, QualityCaseOutcome.NOT_REACHED],
        [QualityCaseType.INSTITUTION]: [QualityCaseOutcome.TALK_DONE],
        [QualityCaseType.CHECK_IN]: [QualityCaseOutcome.TALK_DONE, QualityCaseOutcome.NOT_REACHED],
        [QualityCaseType.RISK]: [QualityCaseOutcome.OK, QualityCaseOutcome.PROBLEM, QualityCaseOutcome.NOT_REACHED],
        [QualityCaseType.COMPLAINT]: [QualityCaseOutcome.SOLVED, QualityCaseOutcome.UNSOLVED],
        [QualityCaseType.TRANSITION]: [
            QualityCaseOutcome.CONTINUES_ONLINE, QualityCaseOutcome.OTHER_INSTITUTION, QualityCaseOutcome.CONTINUES_HERE,
            QualityCaseOutcome.LEAVES, QualityCaseOutcome.NOT_REACHED,
        ],
    };

    /** Onboarding outcomes per target group (cases without onboardingTarget are child cases) */
    export const ByOnboardingTarget: Record<QualityOnboardingTarget, QualityCaseOutcome[]> = {
        [QualityOnboardingTarget.CHILD]: [QualityCaseOutcome.OK, QualityCaseOutcome.PROBLEM, QualityCaseOutcome.NOT_REACHED],
        [QualityOnboardingTarget.TEACHER]: [QualityCaseOutcome.TALK_DONE, QualityCaseOutcome.NOT_REACHED],
        [QualityOnboardingTarget.INSTITUTION]: [QualityCaseOutcome.TALK_DONE, QualityCaseOutcome.NOT_REACHED],
    };

    /** Transition outcomes that count as "übergegangen" (KPI transition rate) */
    export const TRANSITIONED: QualityCaseOutcome[] = [
        QualityCaseOutcome.CONTINUES_ONLINE, QualityCaseOutcome.OTHER_INSTITUTION, QualityCaseOutcome.CONTINUES_HERE,
    ];

    /** Rescue outcomes that count as "gerettet" (KPI rescue rate) */
    export const RESCUED: QualityCaseOutcome[] = [
        QualityCaseOutcome.STAYS, QualityCaseOutcome.OTHER_COURSE, QualityCaseOutcome.ONLINE, QualityCaseOutcome.PAUSED,
    ];
}

export enum QualityCaseAttemptResult {
    REACHED = 'reached',
    NOT_REACHED = 'notReached',
}

/** Case types with several contacts (parents of every child + teacher; Ansprechpartner of the Standort) */
export const QUALITY_MULTI_CONTACT_TYPES: QualityCaseType[] = [QualityCaseType.GROUP, QualityCaseType.INSTITUTION, QualityCaseType.COMPLAINT];

/** Role of a case contact (QualityCase.contacts[]) */
export enum QualityCaseContactRole {
    PARENT = 'parent',
    /** Customer of the child (parent or the child itself) – rescue / onboarding */
    CUSTOMER = 'customer',
    TEACHER = 'teacher',
    /** Ansprechpartner (Responsible) or the Standort itself */
    RESPONSIBLE = 'responsible',
}

export enum QualityCaseContactStatus {
    OPEN = 'open',
    REACHED = 'reached',
    /** maxAttempts "nicht erreicht" reached */
    NOT_REACHED = 'notReached',
    /** Not needed (set by hand) */
    SKIPPED = 'skipped',
}

export namespace QualityCaseContactStatus {
    export const Record: Record<QualityCaseContactStatus, { text: string }> = {
        [QualityCaseContactStatus.OPEN]: {text: 'offen'},
        [QualityCaseContactStatus.REACHED]: {text: 'erreicht'},
        [QualityCaseContactStatus.NOT_REACHED]: {text: 'nicht erreicht'},
        [QualityCaseContactStatus.SKIPPED]: {text: 'übersprungen'},
    };
    export const Items = mapRecordToItems(Record);
}

/** Result of POST quality-cases/:id/contacts/:key/attempt */
export enum QualityCaseContactAttemptResult {
    REACHED = 'reached',
    NOT_REACHED = 'notReached',
    SKIPPED = 'skipped',
    /** Back to open (undo a wrong click) */
    REOPEN = 'reopen',
}

/** Trigger of a group / teacher / Standort / check-in case (QualityCase.trigger) */
export enum QualityCaseGroupTrigger {
    /** Group: a course termination in the course (created together with the rescue case) */
    TERMINATION = 'termination',
    /** Group: nightly scan, active participants below the minimum */
    BELOW_MIN = 'belowMin',
    /** Teacher: real terminations of the teacher's students above the thresholds */
    TERMINATION_SPIKE = 'terminationSpike',
    /** Standort: terminations / open group cases / cancellations above the thresholds */
    PROBLEMS = 'problems',
    /** Teacher: only flags of the Lehrkraft-Scorecard (settings.scorecard.feedTeacherCases), no termination spike */
    SCORECARD = 'scorecard',
    /** Standort: only a low Standort-Gesundheit (settings.health.feedInstitutionCases), no other problem */
    HEALTH = 'health',
    /** Check-in: interval elapsed since the last talk */
    INTERVAL = 'interval',
    /** Check-in: follow-up date set in a talk */
    FOLLOW_UP = 'followUp',
    /** Risk: risk score of the child above the threshold */
    RISK_SCORE = 'riskScore',
    /** Complaint: recorded by hand */
    MANUAL = 'manual',
    /** Transition: grade 4 before the summer holidays */
    GRADE_FOUR = 'gradeFour',
    /** Onboarding / check-in: negative answer or callback wish of an automated feedback request */
    FEEDBACK = 'feedback',
}

/** Category of a complaint (QualityCase.complaint.category) */
export enum QualityComplaintCategory {
    TEACHER = 'teacher',
    ORGANISATION = 'organisation',
    TIMES_ROOM = 'timesRoom',
    BILLING = 'billing',
    OTHER = 'other',
}

export namespace QualityComplaintCategory {
    export const Record: Record<QualityComplaintCategory, { text: string }> = {
        [QualityComplaintCategory.TEACHER]: {text: 'Lehrkraft'},
        [QualityComplaintCategory.ORGANISATION]: {text: 'Organisation'},
        [QualityComplaintCategory.TIMES_ROOM]: {text: 'Zeiten / Raum'},
        [QualityComplaintCategory.BILLING]: {text: 'Abrechnung'},
        [QualityComplaintCategory.OTHER]: {text: 'Sonstiges'},
    };
    export const Items = mapRecordToItems(Record);
}

/** Severity of a complaint */
export enum QualityComplaintSeverity {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
}

export namespace QualityComplaintSeverity {
    export const Record: Record<QualityComplaintSeverity, { text: string }> = {
        [QualityComplaintSeverity.LOW]: {text: 'Gering'},
        [QualityComplaintSeverity.MEDIUM]: {text: 'Mittel'},
        [QualityComplaintSeverity.HIGH]: {text: 'Schwer'},
    };
    export const Items = mapRecordToItems(Record);
}

/** When an onboarding touchpoint is due */
export enum QualityOnboardingTouchpointKind {
    /** value days after the start of the participation */
    DAYS_AFTER_START = 'daysAfterStart',
    /** after the value-th held lesson (timetable: not canceled, not vacant) */
    AFTER_NTH_LESSON = 'afterNthLesson',
    /** value weeks after the start of the participation */
    WEEKS_AFTER_START = 'weeksAfterStart',
    /**
     * In the value-th teaching week from the start (the start week is week 1 when it is a teaching week): calendar weeks
     * without the school / public holiday weeks of the Bundesland (≥ 3 school days Mo–Fr); due on the Friday of that week
     */
    TEACHING_WEEKS_AFTER_START = 'teachingWeeksAfterStart',
}

/**
 * How a routine touchpoint (onboarding / check-in) reaches the person: `call` = case + to-do for the QM person (as before),
 * `message` = automated feedback request (mail / WhatsApp), a case only on negative feedback or a callback wish.
 * Touchpoints stored without channel are calls.
 */
export type QualityContactChannel = 'call' | 'message';
export const QUALITY_CONTACT_CHANNELS: QualityContactChannel[] = ['call', 'message'];

/** Feedback request: why it was sent as a message (the touchpoint itself, or a call turned into a message) */
export type QualityFeedbackChannelReason = 'setting' | 'absence' | 'throttle';

/** Feedback request: routine touchpoint it belongs to (checkIn = recurring contact, also of a child) */
export type QualityFeedbackKind = 'onboarding' | 'checkIn';

/** Touchpoint key of a recurring contact ("Regelmäßig": case touchpoint / feedback touchpoint) */
export const QUALITY_RECURRING_TOUCHPOINT = 'recurring';

/** Recurring contact of one target group ("Regelmäßig": alle X Unterrichtswochen) */
export type QualityRecurringTargetSettings = {
    enabled: boolean;
    /** Teaching weeks (calendar weeks without the holiday weeks of the Bundesland) since the last contact */
    intervalWeeks: number;
    channel: QualityContactChannel;
    /** Open call expires after these days (the next interval counts from its planned date) */
    graceDays: number;
};

/** Channels of a target group: mail only, WhatsApp only, or WhatsApp when possible else mail */
export type QualityFeedbackChannels = 'mail' | 'whatsApp' | 'both';
export const QUALITY_FEEDBACK_CHANNELS: QualityFeedbackChannels[] = ['mail', 'whatsApp', 'both'];

/** Messenger channel of one send */
export type QualityFeedbackSendChannel = 'mail' | 'whatsApp';

export enum QualityFeedbackStatus {
    /** Created by the touchpoint run, waits for the send run (Mo–Fr 10:00) */
    PENDING = 'pending',
    SENT = 'sent',
    ANSWERED = 'answered',
    /** Not answered until expiresAt (KPI only, no case) */
    EXPIRED = 'expired',
    /** Not sent: opt-out, no contact data, no template / sender (skipReason) */
    SKIPPED = 'skipped',
    /** Every channel failed */
    FAILED = 'failed',
}

export namespace QualityFeedbackStatus {
    export const Record: Record<QualityFeedbackStatus, { text: string }> = {
        [QualityFeedbackStatus.PENDING]: {text: 'Geplant'},
        [QualityFeedbackStatus.SENT]: {text: 'Verschickt'},
        [QualityFeedbackStatus.ANSWERED]: {text: 'Beantwortet'},
        [QualityFeedbackStatus.EXPIRED]: {text: 'Keine Antwort'},
        [QualityFeedbackStatus.SKIPPED]: {text: 'Nicht verschickt'},
        [QualityFeedbackStatus.FAILED]: {text: 'Fehlgeschlagen'},
    };
    export const Items = mapRecordToItems(Record);
}

/** Why a feedback request was not sent */
export type QualityFeedbackSkipReason = 'optOut' | 'noContact' | 'noTemplate' | 'noSender' | 'disabled' | 'inactive';

/** Fixed topics of a feedback answer (labels adjustable per target group) */
export enum QualityFeedbackTopic {
    TEACHER = 'teacher',
    TIMES_ROOM = 'timesRoom',
    ORGANISATION = 'organisation',
    MATERIAL = 'material',
    OTHER = 'other',
}

export namespace QualityFeedbackTopic {
    export const Record: Record<QualityFeedbackTopic, { text: string }> = {
        [QualityFeedbackTopic.TEACHER]: {text: 'Lehrkraft'},
        [QualityFeedbackTopic.TIMES_ROOM]: {text: 'Zeiten / Raum'},
        [QualityFeedbackTopic.ORGANISATION]: {text: 'Organisation'},
        [QualityFeedbackTopic.MATERIAL]: {text: 'Material / Buch'},
        [QualityFeedbackTopic.OTHER]: {text: 'Sonstiges'},
    };
    export const Items = mapRecordToItems(Record);
}

/** Default question and topic labels per target group (settings feedback.<target>.question / topics override them) */
export const QUALITY_FEEDBACK_TEXT_DEFAULTS: Record<QualityOnboardingTarget, { question: string; topics: Record<QualityFeedbackTopic, string> }> = {
    [QualityOnboardingTarget.CHILD]: {
        question: 'Wie gefällt Ihrem Kind der Unterricht bisher?',
        topics: {
            [QualityFeedbackTopic.TEACHER]: 'Lehrkraft',
            [QualityFeedbackTopic.TIMES_ROOM]: 'Zeiten / Raum',
            [QualityFeedbackTopic.ORGANISATION]: 'Organisation',
            [QualityFeedbackTopic.MATERIAL]: 'Material / Buch',
            [QualityFeedbackTopic.OTHER]: 'Sonstiges',
        },
    },
    [QualityOnboardingTarget.TEACHER]: {
        question: 'Wie läuft es mit deinem Unterricht bei uns?',
        topics: {
            [QualityFeedbackTopic.TEACHER]: 'Kinder / Gruppe',
            [QualityFeedbackTopic.TIMES_ROOM]: 'Zeiten / Raum',
            [QualityFeedbackTopic.ORGANISATION]: 'Organisation / Büro',
            [QualityFeedbackTopic.MATERIAL]: 'Material / Instrumente',
            [QualityFeedbackTopic.OTHER]: 'Sonstiges',
        },
    },
    [QualityOnboardingTarget.INSTITUTION]: {
        question: 'Wie zufrieden sind Sie mit dem Musikunterricht an Ihrer Schule?',
        topics: {
            [QualityFeedbackTopic.TEACHER]: 'Lehrkraft',
            [QualityFeedbackTopic.TIMES_ROOM]: 'Zeiten / Raum',
            [QualityFeedbackTopic.ORGANISATION]: 'Organisation / Absprachen',
            [QualityFeedbackTopic.MATERIAL]: 'Material',
            [QualityFeedbackTopic.OTHER]: 'Sonstiges',
        },
    },
};

/** Steps of an outcome execution (executedSteps[].step) */
export enum QualityCaseStep {
    /** terminator undo (fixed undo: subject + segment at the course end) */
    UNDO_TERMINATION = 'undoTermination',
    /** course-manager removeStudent from the old course (switch / pause) */
    REMOVE_FROM_COURSE = 'removeFromCourse',
    /** course-manager addStudent to the target course */
    ADD_TO_COURSE = 'addToCourse',
    /** accountant invoiceCycle.update skipCycles of the course subscription items */
    PAUSE_BILLING = 'pauseBilling',
    /** Follow-up case "Rückkehr nach Pause" (pause without seat) – optional */
    CREATE_RETURN_FOLLOW_UP = 'createReturnFollowUp',
    /** accountant invoiceCycle.update profitability snapshot (courseCustomId/institution/subject) – optional */
    UPDATE_RECURRING_SNAPSHOT = 'updateRecurringSnapshot',
    /**
     * accountant makeupRefund.createCredit (same as "+ Gutschrift", usage automatisch): credit for lessons inside the
     * pause of an already invoiced period – only when the QM user chose "Ja"; idempotent per case (requestId)
     */
    CREDIT_BILLED_PAUSE = 'creditBilledPause',
}

/** Defaults of the QM settings (user decisions 2026-10-09) */
/** QualitySettings.group.onTermination */
export type QualityGroupOnTermination = 'always' | 'belowMin' | 'off';
export const QUALITY_GROUP_ON_TERMINATION: QualityGroupOnTermination[] = ['always', 'belowMin', 'off'];

/** 'HH:mm' of a digest time (QualitySettings.notifications.digestTimes) */
export const QUALITY_DIGEST_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export const QUALITY_CASE_DEFAULTS = {
    /**
     * New children. Defaults of a new settings document (stored touchpoints without channel stay calls). A `message`
     * touchpoint is only sent when the feedback of the target group is switched on (feedback.child.enabled + sender),
     * otherwise it is a call as before.
     */
    onboarding: {
        enabled: false,
        touchpoints: [
            {key: 'welcome', label: 'Willkommen', enabled: false, kind: QualityOnboardingTouchpointKind.DAYS_AFTER_START, value: 0, graceDays: 7, channel: 'call' as QualityContactChannel},
            {key: 'afterLesson3', label: 'Nach der 3. Stunde', enabled: true, kind: QualityOnboardingTouchpointKind.AFTER_NTH_LESSON, value: 3, graceDays: 7, channel: 'message' as QualityContactChannel},
            {key: 'week8', label: 'Woche 8', enabled: true, kind: QualityOnboardingTouchpointKind.WEEKS_AFTER_START, value: 8, graceDays: 7, channel: 'message' as QualityContactChannel},
        ],
    },
    /** New teachers (from the first own running course; contact: the teacher) */
    onboardingTeacher: {
        enabled: false,
        touchpoints: [
            {key: 'afterLesson2', label: 'Nach der 2. gehaltenen Stunde', enabled: true, kind: QualityOnboardingTouchpointKind.AFTER_NTH_LESSON, value: 2, graceDays: 7, channel: 'message' as QualityContactChannel},
            {key: 'teachingWeek6', label: 'Unterrichtswoche 6', enabled: true, kind: QualityOnboardingTouchpointKind.TEACHING_WEEKS_AFTER_START, value: 6, graceDays: 7, channel: 'call' as QualityContactChannel},
        ],
    },
    /** New Standorte (from the first running course; contact: Sekretariat + Ansprechpartner) */
    onboardingInstitution: {
        enabled: false,
        touchpoints: [
            {key: 'teachingWeek2', label: 'Unterrichtswoche 2', enabled: true, kind: QualityOnboardingTouchpointKind.TEACHING_WEEKS_AFTER_START, value: 2, graceDays: 7, channel: 'message' as QualityContactChannel},
            {key: 'teachingWeek8', label: 'Unterrichtswoche 8', enabled: true, kind: QualityOnboardingTouchpointKind.TEACHING_WEEKS_AFTER_START, value: 8, graceDays: 7, channel: 'call' as QualityContactChannel},
        ],
    },
    /**
     * Automated feedback requests per target group. Nothing is sent before a target group is switched on AND has a
     * sender (mail address and/or WhatsApp number). WhatsApp only with an APPROVED template and a mobile number.
     */
    feedback: {
        /** One reminder after these days via the same channel(s) */
        reminderDays: 4,
        /** Not answered after these days → expired (KPI only, no case) */
        expiryDays: 14,
        child: {enabled: false, channels: 'both' as QualityFeedbackChannels},
        teacher: {enabled: false, channels: 'both' as QualityFeedbackChannels},
        // Standorte have no mobile number: feedback requests go by mail
        institution: {enabled: false, channels: 'mail' as QualityFeedbackChannels},
    },
    /**
     * Absence and relief: during an absence (QualitySettings.availability.absences) new cases go to the substitute; when
     * nobody is available, call touchpoints become messages. A QM person with more than maxOpenCasesPerQm open cases gets
     * routine call touchpoints (onboarding / check-in) as messages (only target groups with feedback switched on).
     */
    availability: {
        throttleEnabled: true,
        maxOpenCasesPerQm: 25,
    },
    rescue: {
        enabled: false,
        /** Names of the terminationReason taxonomy entries excluded by default (resolved to ids on first load) */
        excludedReasonNames: ['Rückgabe eines Leihobjekts', 'Widerruf'],
        maxAttempts: 3,
    },
    group: {
        enabled: false,
        minParticipants: 3,
        windowDays: 60,
        /** No new group case for a course whose last group case was closed within these days */
        cooldownDays: 30,
        /**
         * Group case when a child of the course terminates: 'belowMin' only when the remaining participants fall below
         * the minimum of the course's format, 'always' for every termination (as before), 'off' never (the nightly scan stays)
         */
        onTermination: 'belowMin' as QualityGroupOnTermination,
    },
    teacher: {
        enabled: false,
        /** ≥ minCount real terminations of the teacher's students within windowDays … */
        minCount: 3,
        windowDays: 30,
        /** … or ≥ ratePercent of the teacher's active students within rateWindowDays */
        ratePercent: 15,
        rateWindowDays: 60,
        cooldownDays: 30,
        /** Open case expires after these days if the alert condition is no longer met */
        graceDays: 21,
    },
    institution: {
        enabled: false,
        minTerminations: 4,
        windowDays: 30,
        minOpenGroupCases: 2,
        minCancellations: 3,
        cancellationWindowWeeks: 6,
        cooldownDays: 30,
        graceDays: 21,
    },
    limits: {
        /** Group / teacher / Standort / onboarding: at most this many new cases per run and QM person (the rest waits for the next run) */
        maxNewCasesPerRunPerQm: 10,
    },
    checkIn: {
        teachersEnabled: false,
        institutionsEnabled: false,
        /** Teaching weeks (calendar weeks without the holiday weeks of the Bundesland) since the last talk */
        teacherIntervalWeeks: 12,
        institutionIntervalWeeks: 20,
        /** Channel of the interval talk: call (case) or message (feedback request; call while the feedback is off) */
        teacherChannel: 'call' as QualityContactChannel,
        institutionChannel: 'message' as QualityContactChannel,
        checkInsPerDayPerQm: 4,
        /** Open check-in expires after these days (the next interval counts from the planned date) */
        graceDays: 14,
    },
    /**
     * "Regelmäßig" per target group (QualitySettings.recurring). Teacher / Standort: migrated from checkIn (teachersEnabled,
     * teacherIntervalWeeks, teacherChannel, graceDays …) while recurring is not stored; children are new (default off).
     * minGapWeeks: a one-time and a recurring contact point of the same target closer than this (teaching weeks) – only the
     * one-time one is created. Load spread: checkIn.checkInsPerDayPerQm (calls; messages × 5).
     */
    recurring: {
        child: {enabled: false, intervalWeeks: 20, channel: 'message' as QualityContactChannel, graceDays: 14},
        teacher: {enabled: false, intervalWeeks: 12, channel: 'call' as QualityContactChannel, graceDays: 14},
        institution: {enabled: false, intervalWeeks: 20, channel: 'message' as QualityContactChannel, graceDays: 14},
        minGapWeeks: 4,
    },
    /** Paket 2 – Risiko-Liste: daily score per active child; cases only with createCases (default off) */
    risk: {
        enabled: false,
        createCases: false,
        /** Score (0–100) from which a child is listed as "at risk" / gets a case */
        threshold: 40,
        /** At most this many new risk cases per QM person and run (highest score first) */
        topNPerQm: 5,
        /** No new risk case for a child whose last risk case was closed within these days */
        cooldownDays: 60,
        /** Open risk case expires after these days */
        graceDays: 14,
        /** Window of the signals (absences, teacher / time change, cancellations, shrinking group, missing logs) */
        windowWeeks: 8,
    },
    /** Paket 2 – Lehrkraft-Scorecard (list + Akte); flags feed the teacher case trigger only with feedTeacherCases */
    scorecard: {
        enabled: false,
        feedTeacherCases: false,
        /** Termination rate (90 days) at least this percent of the average of all teachers … */
        terminationRateFactorPercent: 150,
        /** … and at least this many real terminations */
        minTerminations: 2,
        cancellationRatePercent: 15,
        missingLogsPercent: 30,
    },
    /** Paket 2 – Standort-Gesundheit (list); low score feeds the Standort case trigger only with feedInstitutionCases */
    health: {
        enabled: false,
        feedInstitutionCases: false,
        /** Health score (0–100) below which a Standort counts as "kritisch" */
        lowScore: 50,
    },
    /** Paket 2 – Beschwerden (manual cases, priority high) */
    complaint: {
        enabled: false,
        /** Deadline (Frist) in working days */
        slaWorkingDays: 2,
        /** Open complaint expires after these days ("nicht bearbeitet") */
        graceDays: 30,
    },
    /** Paket 2 – Übergang Klasse 4 / Schulwechsel */
    transition: {
        enabled: false,
        /** Cases are created from this many weeks before the summer holidays (Bundesland of the Standort) */
        weeksBeforeSummer: 8,
        /** Grade that changes school after the summer (Grundschule 4) – fallback for every Bundesland without its own grade */
        grade: 4,
        /** Own grade per Bundesland (StateCodes): primary school ends after grade 6 in Berlin and Brandenburg */
        gradeByState: {BE: 6, BB: 6} as Readonly<Record<string, number>>,
        /** Rescue cases with reason "Schulwechsel": hint + online alternatives */
        schoolChangeHint: false,
        /** Open transition case expires after these days (at the latest at the summer holidays) */
        graceDays: 42,
    },
    /**
     * Notifications of the 15-minute rescue run: instead of one pop-up per case, one summary per QM person at the given
     * Berlin times (Mo–Fr; later cases go into the next slot). Rescue cases whose termination takes effect in fewer than
     * urgentDays days are notified immediately. digestEnabled false: every case notifies immediately (as before).
     */
    notifications: {
        digestEnabled: true,
        digestTimes: ['08:00', '13:00'] as string[],
        urgentDays: 7,
    },
    /**
     * Deprecated (no fixed lookback any more): a child / teacher / Standort counts as new as long as its latest enabled
     * one-time touchpoint + grace has not passed (lookback computed from the settings, capped by contactPointMaxLookbackDays)
     */
    onboardingLookbackDays: 90,
    /** Hard cap of the computed one-time lookback (query cost) */
    contactPointMaxLookbackDays: 730,
    /** Onboarding: default grace of a touchpoint – not created / expired after these days, or once the next one is due */
    onboardingGraceDays: 7,
    /** Onboarding touchpoint key of the follow-up "Rückkehr nach Pause" (never closed as "student left") */
    returnAfterPauseTouchpoint: 'returnAfterPause',
    /** Rescue poll: a termination that keeps failing is given up after these days (the poll window moves on) */
    rescueRetryDays: 2,
    /** Rescue poll: overlap with the previous run */
    rescuePollOverlapMinutes: 60,
    /** Rescue poll: first run (no previous run) looks back this many days */
    rescueInitialLookbackDays: 2,
    /** KPI baseline period (before the cockpit) */
    baselineFrom: '2025-10-01',
    baselineTo: '2026-10-01',
} as const;
